import type { FC, RefObject, JSX } from 'react';
import React, { useEffect, useState } from 'react';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { Button, Iconify } from '@pflegenavi/web-components';
import { Stack } from '@mui/material';
import type { FilePreviewType } from './UploadReceiptCard';
import { useTranslation } from 'react-i18next';
import type { FormikProps } from 'formik';
import { HiddenInputForReceiptFile } from './HiddenInputForReceiptFile';
import { ImagePreviewContainer } from './ImagePreviewContainer';
import { ImagesPreviewNavigationBar } from './imagesHandlingCard/ImagesPreviewNavigationBar';

const IMAGES_IN_ROW = 4;

interface ImagesHandlingFormProps<V extends { receiptImageIds: string[] }> {
  formik: FormikProps<V>;
  selectedImageIndex: number;
  receiptImageUrls: string[];
  imageUrlToTypeMap: UrlTypeNameMapping;
  file: FilePreviewType;
  setFile: (file: any) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  onAddFile: (e: any) => void;
  onDeleteFile: VoidFunction;
  onOpenSelectImageDialog: () => void;
  onSelectPrevious: () => void;
  onSelectNext: () => void;
  onSelectDifferentImage: (imageUrl: string) => void;
  canEdit: boolean;
  onChangeFile: () => void;
}

export const ImagesHandlingForm = <
  V extends {
    receiptImageIds: string[];
  }
>({
  receiptImageUrls,
  imageUrlToTypeMap,
  file,
  canEdit,
  onAddFile,
  onDeleteFile,
  inputRef,
  onOpenSelectImageDialog,
  selectedImageIndex,
  onSelectDifferentImage,
  onChangeFile,
  onSelectPrevious,
  onSelectNext,
}: ImagesHandlingFormProps<V>): JSX.Element => {
  const [firstImageIndex, setFirstImageIndex] = useState(0);

  const currentImageUrl = receiptImageUrls[selectedImageIndex];
  const imageUrlsToDisplay =
    receiptImageUrls.length <= IMAGES_IN_ROW
      ? receiptImageUrls
      : receiptImageUrls.slice(
          firstImageIndex,
          IMAGES_IN_ROW + firstImageIndex
        );

  useEffect(() => {
    if (!imageUrlsToDisplay.includes(currentImageUrl)) {
      if (selectedImageIndex + IMAGES_IN_ROW <= receiptImageUrls.length) {
        setFirstImageIndex(selectedImageIndex);
      } else {
        setFirstImageIndex(selectedImageIndex - (IMAGES_IN_ROW - 1));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentImageUrl,
    receiptImageUrls.length,
    selectedImageIndex /* imageUrlsToDisplay intentionally left out */,
  ]);

  const onSelectPreviousNavBar = () => {
    setFirstImageIndex(firstImageIndex - 1);
  };

  const onSelectNextNavBar = () => {
    setFirstImageIndex(firstImageIndex + 1);
  };

  return (
    <Stack>
      <HiddenInputForReceiptFile inputRef={inputRef} onChangeFile={onAddFile} />
      <ImagesPreviewNavigationBar
        imageUrls={receiptImageUrls}
        imageUrlToTypeMap={imageUrlToTypeMap}
        onSelectPrevious={onSelectPreviousNavBar}
        onSelectNext={onSelectNextNavBar}
        onSelectDifferentImage={onSelectDifferentImage}
        selectedImageUrl={currentImageUrl}
        imageUrlsToDisplay={imageUrlsToDisplay}
      />
      {canEdit && <AddReceiptButton onClick={onOpenSelectImageDialog} />}
      <ImagePreviewContainer
        canEdit={canEdit}
        inputRef={inputRef}
        currentImageUrl={currentImageUrl}
        onSelectPrevious={onSelectPrevious}
        onSelectNext={onSelectNext}
        onDelete={onDeleteFile}
        imageUrls={receiptImageUrls}
        imageUrlToTypeMap={imageUrlToTypeMap}
        currentImageIndex={selectedImageIndex}
        file={file}
        onAddFile={onAddFile}
        onChangeFile={onChangeFile}
      />
    </Stack>
  );
};

interface AddReceiptButtonProps {
  onClick: () => void;
}

export const AddReceiptButton: FC<AddReceiptButtonProps> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="flex-start" mt={1} mb={1}>
      <Button
        color="primary"
        variant="text"
        startIcon={<Iconify icon="eva:plus-fill" />}
        onClick={onClick}
      >
        {t('receipt.upload.additionalReceipt')}
      </Button>
    </Stack>
  );
};
